import React from 'react';
import styled from 'astroturf/react';

import { Icon } from 'js/design_system/Icon';

export type TrendVariant = 'chip' | 'icon';

export type TrendAdornmentProps = {
  variant: TrendVariant;
  pct: number | null;
};

export const FLAT_THRESHOLD_PCT = 1.0;

type Direction = 'up' | 'down' | 'flat';

const Chip = styled.span<{ direction: 'up' | 'down' }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  padding: 0 3px;
  border-radius: 7px;
  font-size: 10px;
  font-weight: 600;
  line-height: 14px;
  white-space: nowrap;

  &.direction-up {
    color: var(--success-dark, #05b01c);
    background: var(--success-light, #f0f8f1);
  }

  &.direction-down {
    color: var(--error-dark, #b60000);
    background: var(--error-light, #fff2f2);
  }
`;

const IconWrap = styled.span<{ direction: Direction }>`
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;

  &.direction-up {
    color: var(--success-dark, #05b01c);
  }

  &.direction-down {
    color: var(--error-dark, #b60000);
  }

  &.direction-flat {
    color: var(--text-secondary, #535366);
  }
`;

const ICON_NAMES: Record<Direction, string> = {
  up: 'trending_up',
  down: 'trending_down',
  flat: 'trending_flat',
};

export function formatTrendPct(pct: number): string {
  const rounded = Number(pct.toFixed(1)) + 0;
  const sign = rounded >= 0 ? '+' : '';
  return `${sign}${rounded.toFixed(1)}%`;
}

function iconDirection(pct: number): Direction {
  if (Math.abs(pct) < FLAT_THRESHOLD_PCT) {
    return 'flat';
  }
  return pct > 0 ? 'up' : 'down';
}

const TrendAdornment = ({ variant, pct }: TrendAdornmentProps) => {
  if (pct == null) {
    return null;
  }

  if (variant === 'chip') {
    const text = formatTrendPct(pct);
    const direction = text.startsWith('+') ? 'up' : 'down';
    return (
      <Chip
        data-testid="trend-chip"
        data-direction={direction}
        direction={direction}
      >
        {text}
      </Chip>
    );
  }

  const direction = iconDirection(pct);
  return (
    <IconWrap
      data-testid="trend-icon"
      data-direction={direction}
      direction={direction}
    >
      <Icon
        name={ICON_NAMES[direction]}
        size={16}
        role="img"
        aria-label={`Trend: ${direction}`}
      />
    </IconWrap>
  );
};

export default TrendAdornment;
